/**
 * InvoiceDetailModal (Phase D1.1-C · Feb 2026)
 * --------------------------------------------
 * Read-only view of an Invoice with payment logging + credit-note flows.
 *
 * Sections:
 *   • Header: number, status, origin estimate link
 *   • Action bar: PDF, Print, (admin) Cancel/Reactivate/Due-on-Delivery
 *   • Items snapshot (immutable, from origin estimate)
 *   • Totals + emphasized Balance Due
 *   • Payments log + "Registrar pago" inline form
 *   • Credit Notes list + "Emitir nota de crédito" (admin)
 *   • History audit trail
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import PartnerDeliveryBlock from "@/components/PartnerDeliveryBlock";
import { useI18n } from "@/context/I18nContext";
import { groupItemsByProduct } from "@/lib/groupItems";
import OrderDetailsModal from "@/components/OrderDetailsModal";
import { ClientFilesSection } from "@/components/EstimateDetailModal";
import { PartnerPointBlock } from "@/components/PartnerPointBlock";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_LABEL_KEYS = {
  unpaid: "inv_status_unpaid",
  partial_paid: "inv_status_partial_paid",
  paid: "inv_status_paid",
  due_on_delivery: "inv_status_due_on_delivery_full",
  cancelled: "inv_status_cancelled",
  credited: "inv_status_credited",
};
const STATUS_TONE = {
  unpaid: "bg-amber-200 text-amber-900",
  partial_paid: "bg-cyan-200 text-cyan-900",
  paid: "bg-emerald-200 text-emerald-900",
  due_on_delivery: "bg-purple-200 text-purple-900",
  cancelled: "bg-red-200 text-red-900",
  credited: "bg-neutral-300 text-neutral-800",
};

export default function InvoiceDetailModal({
  invoiceId,
  onClose,
  onChanged,
  settings,
}) {
  const { isAdmin, hasOfficialPermission, officialPerms } = useAuth();
  // Bloque 1 Partner Points — gate de visibilidad para registrar pagos:
  // admin, permiso oficial payments.register_own, o usuario legacy puro
  // (sin permisos oficiales — retrocompat). El backend ya bloquea (403).
  const canRegisterPayments =
    isAdmin ||
    hasOfficialPermission("payments.register_own") ||
    !((officialPerms?.permissions || []).length);
  const { t } = useI18n();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [cnOpen, setCnOpen] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: "",
    payment_method: "",
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [cnForm, setCnForm] = useState({
    amount: "",
    reason: "",
    payment_method: "",
    settlement_type: "refund",
  });
  // GP 1.0 — Modal para ver la Work Order asociada
  const [woModalOpen, setWoModalOpen] = useState(false);
  const [woOrder, setWoOrder] = useState(null);
  const [woEmployees, setWoEmployees] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Feb 2026 · bugfix aditivo: el fetch de credit-notes es admin-only
      // en el backend. Para roles operativos (p. ej. Ventas) devuelve 403;
      // no debe abortar la carga del detalle de la Invoice. Tratamos ese
      // request como opcional y caemos a lista vacía si falla.
      const invRes = await axios.get(`${API}/commercial/invoices/${invoiceId}`, {
        withCredentials: true,
      });
      setInv(invRes.data);
      try {
        const cnRes = await axios.get(`${API}/commercial/credit-notes`, {
          params: { invoice_id: invoiceId },
          withCredentials: true,
        });
        setCredits(cnRes.data || []);
      } catch {
        setCredits([]);
      }
    } catch (e) {
      toast.error(t("inv_detail_load_err"));
      onClose();
    } finally {
      setLoading(false);
    }
    // Deps: sólo invoiceId — mismo P0 fix que EstimateDetailModal (el poller
    // de alertas re-renderizaba al padre y la recarga borraba lo escrito).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  const acceptedMethods = settings?.accepted_payment_methods || [
    "Cash",
    "Card",
    "Check",
    "Zelle",
    "ACH",
  ];
  const availableStoreCredit = Number(inv?.available_store_credit || 0);
  const paymentMethods = availableStoreCredit > 0
    ? [...acceptedMethods, "Store Credit"]
    : acceptedMethods;

  const logAction = async (action, meta = {}) => {
    try {
      await axios.post(
        `${API}/commercial/invoices/${invoiceId}/log-action`,
        { action, meta },
        { withCredentials: true }
      );
    } catch {
      /* non-fatal */
    }
  };

  const submitPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) {
      toast.error(t("inv_detail_pay_invalid_amount"));
      return;
    }
    if (!payForm.payment_method) {
      toast.error(t("inv_detail_pay_missing_method"));
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/commercial/invoices/${invoiceId}/payments`,
        {
          amount: Number(payForm.amount),
          payment_method: payForm.payment_method,
          date: payForm.date,
          notes: payForm.notes,
        },
        { withCredentials: true }
      );
      setInv(data);
      setPayForm({
        amount: "",
        payment_method: "",
        date: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      setPayOpen(false);
      toast.success(t("inv_detail_pay_ok"));
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("inv_detail_pay_err"));
    }
  };

  const submitCreditNote = async () => {
    if (!cnForm.amount || Number(cnForm.amount) <= 0) {
      toast.error(t("inv_detail_pay_invalid_amount"));
      return;
    }
    try {
      await axios.post(
        `${API}/commercial/credit-notes`,
        {
          invoice_id: invoiceId,
          amount: Number(cnForm.amount),
          reason: cnForm.reason,
          payment_method: cnForm.settlement_type === "refund" ? cnForm.payment_method : "",
          settlement_type: cnForm.settlement_type || "refund",
        },
        { withCredentials: true }
      );
      setCnForm({ amount: "", reason: "", payment_method: "", settlement_type: "refund" });
      setCnOpen(false);
      toast.success(
        cnForm.settlement_type === "store_credit"
          ? t("inv_detail_cn_sc_ok")
          : t("inv_detail_cn_ok")
      );
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("inv_detail_cn_err"));
    }
  };

  const doAdminAction = async (path, body = {}) => {
    try {
      const { data } = await axios.post(
        `${API}/commercial/invoices/${invoiceId}/${path}`,
        body,
        { withCredentials: true }
      );
      setInv(data);
      onChanged && onChanged();
      toast.success(t("inv_detail_admin_ok"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("inv_detail_admin_err"));
    }
  };

  // GP 1.0 — Puente Comercial → Taller. Crea (o recupera) la Work Order
  // legacy vinculada a esta factura para que aparezca en Producción, y
  // abre automáticamente el detalle de la orden para que el usuario la
  // vea, valide y confirme que llegó al taller.
  const openWorkOrderById = async (woId) => {
    try {
      const [{ data: orders }, empRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const found = (orders || []).find((o) => o.id === woId);
      if (!found) {
        toast.error(t("inv_detail_wo_not_found"));
        return;
      }
      setWoEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      setWoOrder(found);
      setWoModalOpen(true);
    } catch (e) {
      toast.error(t("inv_detail_wo_open_err"));
    }
  };

  const createWorkOrder = async () => {
    try {
      const { data } = await axios.post(
        `${API}/commercial/invoices/${invoiceId}/create-work-order`,
        {},
        { withCredentials: true }
      );
      if (data.already_existed) {
        toast.success(t("inv_detail_wo_existed", { n: data.work_order_number }));
      } else {
        toast.success(t("inv_detail_wo_created", { n: data.work_order_number }));
      }
      await load();
      onChanged && onChanged();
      // Auto-abrir el detalle de la WO para que el usuario la vea.
      await openWorkOrderById(data.work_order_id);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("inv_detail_wo_create_err")
      );
    }
  };

  const downloadPdf = async () => {
    // Feb 2026 — ventana síncrona en el clic (anti popup-blocker).
    const w = window.open("", "_blank");
    try {
      const res = await axios.get(`${API}/commercial/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      if (w) w.location = url;
      else window.open(url, "_blank");
      logAction("pdf_generated");
    } catch {
      if (w) w.close();
      toast.error(t("inv_detail_pdf_err"));
    }
  };

  const printPdf = async () => {
    // Feb 2026 — ventana síncrona en el clic (anti popup-blocker).
    const win = window.open("", "_blank");
    try {
      const res = await axios.get(`${API}/commercial/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      if (win) {
        win.addEventListener("load", () => {
          try {
            win.focus();
            win.print();
          } catch {
            /* ignore */
          }
        });
        win.location = url;
      } else {
        window.open(url, "_blank");
      }
      logAction("printed");
    } catch {
      if (win) win.close();
      toast.error(t("inv_detail_print_err"));
    }
  };

  const sendWhatsapp = async () => {
    if (!inv) return;
    const phone = (inv.customer_phone || inv.phone || "").replace(/[^\d]/g, "");
    // Feb 2026 — link público con token secreto (share_token), mismo patrón
    // que Estimates. Ventana abierta síncronamente (popup blocker).
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.post(
        `${API}/commercial/invoices/${inv.id}/share-token`,
        {},
        { withCredentials: true }
      );
      const shareUrl = `${window.location.origin}/?i=${data.token}`;
      const brand = settings?.company_name || "Rawen's";
      const msg = `Hola ${inv.contact_person || ""}, aquí está tu Invoice ${inv.number} de ${brand}. Total: $${Number(inv.total ?? inv.total_amount ?? 0).toFixed(2)}.\n${shareUrl}\nGracias.`;
      const url = phone
        ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      if (w) w.location = url;
      else window.open(url, "_blank");
      logAction("whatsapped");
    } catch {
      if (w) w.close();
      toast.error("No se pudo generar el enlace para compartir");
    }
  };

  const sendEmail = async () => {
    try {
      await axios.post(
        `${API}/commercial/invoices/${invoiceId}/email`,
        {},
        { withCredentials: true }
      );
      toast.success(t("inv_detail_email_ok"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("inv_detail_email_err"));
    }
  };

  const downloadCnPdf = async (cnId) => {
    try {
      const res = await axios.get(`${API}/commercial/credit-notes/${cnId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error(t("inv_detail_cn_pdf_err"));
    }
  };

  const status = inv?.status;
  const isCancelled = status === "cancelled";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto"
      onClick={onClose}
    >
      <div
        data-testid="invoice-detail-modal"
        className="bg-white w-full max-w-4xl border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !inv ? (
          <div className="p-10 text-center font-mono-tech text-sm text-[var(--text-secondary)]">
            {t("loading_ellipsis")}
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="p-5 border-b border-black flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {settings?.company_logo_url && (
                  <img
                    src={settings.company_logo_url}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    className="w-14 h-14 object-contain border border-[var(--border)] bg-white p-1"
                  />
                )}
                <div>
                  <div className="label-overline">{t("inv_detail_label")}</div>
                  <div className="flex items-center gap-3 mt-1">
                    <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
                      {inv.number}
                    </h2>
                    <span
                      data-testid="invoice-detail-status"
                      className={`font-mono-tech text-xs font-bold tracking-wider px-2 py-1 ${
                        STATUS_TONE[status] || "bg-neutral-100"
                      }`}
                    >
                      {STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] mt-1">
                    {t("inv_detail_origin_label")}{" "}
                    <span className="font-mono-tech font-bold">
                      {inv.origin_estimate_number}
                    </span>
                    {" · "}
                    {inv.company} · {inv.contact_person}
                  </p>
                  {inv.partner_point && (
                    <div className="mt-3 max-w-md">
                      <PartnerPointBlock pp={inv.partner_point} compact />
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="invoice-detail-close"
                className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
              >
                {t("close")}
              </button>
            </header>

            {/* Action bar */}
            <div className="p-4 border-b border-[var(--border)] flex flex-wrap gap-2">
              <ActionBtn testid="invoice-detail-pdf" onClick={downloadPdf}>
                {t("action_pdf")}
              </ActionBtn>
              <ActionBtn testid="invoice-detail-print" onClick={printPdf}>
                {t("action_print")}
              </ActionBtn>
              <ActionBtn testid="invoice-detail-email" onClick={sendEmail}>
                {t("action_email_icon")}
              </ActionBtn>
              <ActionBtn testid="invoice-detail-whatsapp" onClick={sendWhatsapp}>
                {t("action_whatsapp")}
              </ActionBtn>
              {canRegisterPayments && !isCancelled && status !== "paid" && status !== "credited" && (
                <ActionBtn
                  testid="invoice-detail-add-payment"
                  primary
                  onClick={() => setPayOpen((v) => !v)}
                >
                  {t("inv_detail_add_payment_btn")}
                </ActionBtn>
              )}
              {/* GP 1.0 — Puente al Taller. Crear/ver Work Order es
                  admin-only en backend (`_dep_admin_only` en
                  `POST /commercial/invoices/{id}/create-work-order`).
                  Ocultamos el botón para roles operativos (p. ej. Ventas)
                  para evitar 403 y ruido de UX. */}
              {isAdmin && !isCancelled && (
                inv.work_order_id ? (
                  <ActionBtn
                    testid="invoice-detail-wo-link"
                    onClick={createWorkOrder}
                  >
                    {t("inv_detail_wo_link_btn", { n: inv.work_order_number })}
                  </ActionBtn>
                ) : (
                  <ActionBtn
                    testid="invoice-detail-create-wo"
                    primary
                    onClick={createWorkOrder}
                  >
                    {t("inv_detail_create_wo_btn")}
                  </ActionBtn>
                )
              )}
              {isAdmin && !isCancelled && status !== "credited" && (
                <ActionBtn
                  testid="invoice-detail-add-cn"
                  onClick={() => setCnOpen((v) => !v)}
                >
                  {t("inv_detail_issue_cn_btn")}
                </ActionBtn>
              )}
              {isAdmin && !isCancelled && status !== "paid" && status !== "credited" && (
                <ActionBtn
                  testid="invoice-detail-dod"
                  onClick={() => {
                    const reason = window.prompt(t("inv_detail_dod_prompt"));
                    if (reason !== null) doAdminAction("due-on-delivery", { reason });
                  }}
                >
                  {t("inv_detail_dod_btn")}
                </ActionBtn>
              )}
              {isAdmin && !isCancelled && (
                <ActionBtn
                  testid="invoice-detail-cancel"
                  danger
                  onClick={() => {
                    if (window.confirm(t("inv_detail_cancel_confirm")))
                      doAdminAction("cancel");
                  }}
                >
                  {t("action_cancel_icon")}
                </ActionBtn>
              )}
              {isAdmin && isCancelled && (
                <ActionBtn
                  testid="invoice-detail-reactivate"
                  primary
                  onClick={() => {
                    const reason = window.prompt(t("inv_detail_reactivate_prompt"));
                    if (reason !== null) doAdminAction("reactivate", { reason });
                  }}
                >
                  {t("action_reactivate_icon")}
                </ActionBtn>
              )}
            </div>

            {/* Bloque 5A — Traslado/entrega Partner Point (se auto-oculta) */}
            {inv.work_order_id && (
              <PartnerDeliveryBlock orderId={inv.work_order_id} admin={isAdmin} />
            )}

            {/* Store Credit banner */}
            {availableStoreCredit > 0 && inv.balance_due > 0 && !isCancelled && (
              <div
                data-testid="invoice-detail-store-credit-banner"
                className="p-4 bg-emerald-50 border-b border-emerald-300 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-widest text-emerald-800">
                    {t("inv_detail_sc_banner_title")}
                  </div>
                  <div className="text-sm mt-1">
                    {t("inv_detail_sc_banner_msg_a")}
                    <b data-testid="invoice-detail-store-credit-amount">
                      {money(availableStoreCredit)}
                    </b>
                    {t("inv_detail_sc_banner_msg_b")}
                  </div>
                </div>
                <button
                  data-testid="invoice-detail-apply-store-credit"
                  onClick={() => {
                    const applicable = Math.min(availableStoreCredit, Number(inv.balance_due || 0));
                    setPayForm((f) => ({
                      ...f,
                      amount: applicable.toFixed(2),
                      payment_method: "Store Credit",
                      notes: f.notes || t("inv_detail_sc_apply_notes"),
                    }));
                    setPayOpen(true);
                  }}
                  className="font-mono-tech text-xs font-bold border border-emerald-800 bg-emerald-800 text-white px-4 py-2 hover:opacity-90"
                >
                  {t("inv_detail_sc_apply_btn")}
                </button>
              </div>
            )}

            {/* Payment form */}
            {payOpen && (
              <div
                data-testid="invoice-detail-payment-form"
                className="p-4 bg-cyan-50 border-b border-[var(--border)] space-y-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <Field label={t("inv_detail_pay_form_amount")}>
                    <input
                      data-testid="payment-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={payForm.amount}
                      onChange={(e) =>
                        setPayForm((f) => ({ ...f, amount: e.target.value }))
                      }
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                      placeholder={money(inv.balance_due)}
                    />
                  </Field>
                  <Field label={t("inv_detail_pay_form_method")}>
                    <select
                      data-testid="payment-method"
                      value={payForm.payment_method}
                      onChange={(e) =>
                        setPayForm((f) => ({ ...f, payment_method: e.target.value }))
                      }
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-white"
                    >
                      <option value="">{t("inv_detail_pay_form_method_placeholder")}</option>
                      {paymentMethods.map((m) => (
                        <option key={m} value={m}>
                          {m}
                          {m === "Store Credit" ? ` (${money(availableStoreCredit)})` : ""}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("inv_detail_pay_form_date")}>
                    <input
                      data-testid="payment-date"
                      type="date"
                      value={payForm.date}
                      onChange={(e) =>
                        setPayForm((f) => ({ ...f, date: e.target.value }))
                      }
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label={t("inv_detail_pay_form_notes")}>
                    <input
                      data-testid="payment-notes"
                      value={payForm.notes}
                      onChange={(e) =>
                        setPayForm((f) => ({ ...f, notes: e.target.value }))
                      }
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                      placeholder={t("inv_detail_pay_form_notes_placeholder")}
                    />
                  </Field>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="payment-submit"
                    onClick={submitPayment}
                    className="font-mono-tech text-xs font-bold border border-black bg-black text-white px-4 py-2 hover:opacity-90"
                  >
                    {t("inv_detail_pay_form_submit")}
                  </button>
                  <button
                    onClick={() => setPayOpen(false)}
                    className="font-mono-tech text-xs border border-[var(--border)] px-4 py-2 hover:bg-black hover:text-white"
                  >
                    {t("cancel")}
                  </button>
                </div>
              </div>
            )}

            {/* Credit note form */}
            {cnOpen && (
              <div
                data-testid="invoice-detail-cn-form"
                className="p-4 bg-rose-50 border-b border-[var(--border)] space-y-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="TIPO DE LIQUIDACIÓN">
                    <div className="flex gap-2 mt-1">
                      <label className={`flex-1 border px-3 py-2 text-xs font-mono-tech font-bold cursor-pointer text-center ${cnForm.settlement_type === "refund" ? "bg-black text-white border-black" : "border-[var(--border)]"}`}>
                        <input
                          data-testid="cn-settlement-refund"
                          type="radio"
                          name="cn-settlement"
                          value="refund"
                          checked={cnForm.settlement_type === "refund"}
                          onChange={() => setCnForm((f) => ({ ...f, settlement_type: "refund" }))}
                          className="hidden"
                        />
                        Reembolso
                      </label>
                      <label className={`flex-1 border px-3 py-2 text-xs font-mono-tech font-bold cursor-pointer text-center ${cnForm.settlement_type === "store_credit" ? "bg-black text-white border-black" : "border-[var(--border)]"}`}>
                        <input
                          data-testid="cn-settlement-store-credit"
                          type="radio"
                          name="cn-settlement"
                          value="store_credit"
                          checked={cnForm.settlement_type === "store_credit"}
                          onChange={() => setCnForm((f) => ({ ...f, settlement_type: "store_credit" }))}
                          className="hidden"
                        />
                        Store Credit
                      </label>
                    </div>
                    <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                      {cnForm.settlement_type === "store_credit"
                        ? "Se abonará al saldo del cliente para futuras compras."
                        : "Se registra la devolución de dinero (efectivo, tarjeta, cheque, etc.)."}
                    </p>
                  </Field>
                  <Field label="MONTO">
                    <input
                      data-testid="cn-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={cnForm.amount}
                      onChange={(e) => setCnForm((f) => ({ ...f, amount: e.target.value }))}
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cnForm.settlement_type === "refund" && (
                    <Field label="MÉTODO DE REEMBOLSO (opcional)">
                      <select
                        data-testid="cn-method"
                        value={cnForm.payment_method}
                        onChange={(e) =>
                          setCnForm((f) => ({ ...f, payment_method: e.target.value }))
                        }
                        className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-white"
                      >
                        <option value="">— (Ninguno) —</option>
                        {acceptedMethods.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                  <Field label="MOTIVO">
                    <input
                      data-testid="cn-reason"
                      value={cnForm.reason}
                      onChange={(e) => setCnForm((f) => ({ ...f, reason: e.target.value }))}
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                      placeholder="Ej: Ajuste por daño"
                    />
                  </Field>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="cn-submit"
                    onClick={submitCreditNote}
                    className="font-mono-tech text-xs font-bold border border-black bg-black text-white px-4 py-2 hover:opacity-90"
                  >
                    Emitir CN
                  </button>
                  <button
                    onClick={() => setCnOpen(false)}
                    className="font-mono-tech text-xs border border-[var(--border)] px-4 py-2 hover:bg-black hover:text-white"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Body */}
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 space-y-4">
                <Section title="Cliente">
                  <div className="text-base space-y-0.5">
                    <div>
                      <b>{inv.company}</b>
                    </div>
                    <div>{inv.contact_person}</div>
                    <div>{inv.customer_phone}</div>
                    <div>{inv.customer_email}</div>
                  </div>
                </Section>

                <Section title="Meta">
                  <div className="text-sm grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">Oficina: </span>
                      {inv.office_name || inv.office_key}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">Sales Rep: </span>
                      {inv.sales_rep_name}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">Creada: </span>
                      {String(inv.created_at || "").slice(0, 10)}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">Entrega: </span>
                      {inv.delivery_date || "—"}
                    </div>
                    {inv.paid_at && (
                      <div>
                        <span className="text-[var(--text-secondary)] text-xs">Pagada: </span>
                        {String(inv.paid_at || "").slice(0, 10)}
                      </div>
                    )}
                    {inv.cancelled_at && (
                      <div>
                        <span className="text-[var(--text-secondary)] text-xs">Cancelada: </span>
                        {String(inv.cancelled_at || "").slice(0, 10)}
                      </div>
                    )}
                  </div>
                </Section>

                <Section title="Ítems (snapshot inmutable)">
                  <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                    {groupItemsByProduct(inv.items || []).map((g) => {
                      if (g.variants.length === 1) {
                        const it = g.variants[0];
                        return (
                          <div
                            key={g.key}
                            data-testid={`invoice-detail-item-row-${it.__originalIndex}`}
                            className="p-3 flex items-center gap-3"
                          >
                            {it.catalog_image_url && (
                              <img
                                src={it.catalog_image_url}
                                alt=""
                                className="w-10 h-10 object-contain border border-[var(--border)] bg-white"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold truncate">
                                {it.description || it.product_code}
                              </div>
                              <div className="text-xs text-[var(--text-secondary)]">
                                {[it.catalog_color, it.catalog_size].filter(Boolean).join(" · ")}
                              </div>
                              {it.customer_notes && (
                                <div
                                  data-testid={`invoice-detail-item-note-${it.__originalIndex}`}
                                  className="text-xs italic text-[var(--text-secondary)] mt-0.5"
                                >
                                  Nota: {it.customer_notes}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-right w-14">×{it.quantity}</div>
                            <div className="text-sm text-right w-20">{money(it.unit_price)}</div>
                            <div className="text-sm text-right w-24 font-bold">
                              {money(it.subtotal)}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={g.key}
                          data-testid={`invoice-detail-item-group-${g.key}`}
                          className="p-3"
                        >
                          <div className="flex items-center gap-3">
                            {g.header.catalog_image_url && (
                              <img
                                src={g.header.catalog_image_url}
                                alt=""
                                className="w-10 h-10 object-contain border border-[var(--border)] bg-white"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold truncate">
                                {g.header.description}
                              </div>
                              <div className="text-xs text-[var(--text-secondary)]">
                                {t("item_group_variants_count", { n: g.variants.length })} ·{" "}
                                {t("item_group_total_units", { q: g.totalQty })}
                              </div>
                              {g.header.customer_notes && (
                                <div
                                  data-testid={`invoice-detail-group-note-${g.key}`}
                                  className="text-xs italic text-[var(--text-secondary)] mt-0.5"
                                >
                                  Nota: {g.header.customer_notes}
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-right w-20">{money(g.header.unit_price)}</div>
                            <div className="text-sm text-right w-24 font-bold">
                              {money(g.totalAmount)}
                            </div>
                          </div>
                          <div className="mt-2 ml-13 pl-3 border-l-2 border-[var(--border)] divide-y divide-[var(--border)]">
                            {g.variants.map((it) => (
                              <div
                                key={it.__originalIndex}
                                data-testid={`invoice-detail-item-row-${it.__originalIndex}`}
                                className="py-1.5 flex items-center gap-3 text-xs text-[var(--text-secondary)]"
                              >
                                <div className="flex-1 min-w-0 truncate">
                                  {[it.catalog_color, it.catalog_size].filter(Boolean).join(" · ") || "—"}
                                </div>
                                <div className="text-right w-14">×{it.quantity}</div>
                                <div className="text-right w-24 font-bold text-[var(--text)]">
                                  {money(it.subtotal)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Section>

                {isAdmin && (
                  <Section title={t("inv_cost_section")}>
                    <div
                      data-testid="invoice-cost-editor"
                      className="border border-amber-300 divide-y divide-[var(--border)]"
                    >
                      {(inv.items || []).map((it, idx) => (
                        <ItemCostRow
                          key={it.id || idx}
                          invoiceId={inv.id}
                          item={it}
                          idx={idx}
                          t={t}
                          onSaved={() => {
                            load();
                            onChanged && onChanged();
                          }}
                        />
                      ))}
                    </div>
                  </Section>
                )}

                <Section title={`Pagos registrados (${(inv.payments || []).length})`}>
                  {(inv.payments || []).length === 0 ? (
                    <div className="text-sm text-[var(--text-secondary)]">Aún no hay pagos.</div>
                  ) : (
                    <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                      {inv.payments.map((p) => (
                        <div
                          key={p.id}
                          data-testid={`invoice-detail-payment-${p.id}`}
                          className="p-3 flex items-center gap-3"
                        >
                          <div className="flex-1">
                            <div className="text-sm">
                              <b>{p.payment_method}</b> · {p.date}
                            </div>
                            {p.notes && (
                              <div className="text-xs text-[var(--text-secondary)]">
                                {p.notes}
                              </div>
                            )}
                            <div className="text-xs text-[var(--text-secondary)]">
                              registrado por {p.recorded_by_name}
                            </div>
                          </div>
                          <div className="text-base font-mono-tech font-bold text-emerald-700">
                            {money(p.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                <Section title={`Notas de crédito (${credits.length})`}>
                  {credits.length === 0 ? (
                    <div className="text-sm text-[var(--text-secondary)]">
                      Sin notas de crédito.
                    </div>
                  ) : (
                    <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                      {credits.map((c) => (
                        <div
                          key={c.id}
                          data-testid={`invoice-detail-cn-${c.number}`}
                          className="p-3 flex items-center gap-3"
                        >
                          <div className="flex-1">
                            <div className="text-sm">
                              <b>{c.number}</b> · {String(c.created_at || "").slice(0, 10)}
                              {c.payment_method ? ` · ${c.payment_method}` : ""}
                            </div>
                            {c.reason && (
                              <div className="text-xs text-[var(--text-secondary)]">
                                {c.reason}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => downloadCnPdf(c.id)}
                            className="font-mono-tech text-[0.65rem] font-bold border border-[var(--border)] px-2 py-1 hover:bg-black hover:text-white"
                          >
                            PDF
                          </button>
                          <div className="text-base font-mono-tech font-bold text-rose-700">
                            -{money(c.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                {inv.customer_notes && (
                  <Section title="Notas al cliente">
                    <div className="text-sm whitespace-pre-wrap">{inv.customer_notes}</div>
                  </Section>
                )}
              </div>

              <div className="space-y-4">
                <Section title="Resumen">
                  <div className="bg-black text-white p-4 space-y-2 font-mono-tech">
                    <Row label="Subtotal" value={money(inv.subtotal)} />
                    {inv.discount_amount > 0 && (
                      <Row
                        label="Descuento"
                        value={`-${money(inv.discount_amount)}`}
                      />
                    )}
                    <Row
                      label={
                        inv.tax_exempt
                          ? "Tax (Exento)"
                          : `Sales Tax (${(inv.sales_tax_rate * 100).toFixed(2)}%)`
                      }
                      value={money(inv.sales_tax_amount)}
                    />
                    <div className="border-t border-white/20 pt-2 flex justify-between text-xl font-bold">
                      <span>TOTAL</span>
                      <span data-testid="invoice-detail-total">
                        {money(inv.total_amount)}
                      </span>
                    </div>
                    {inv.payments_total > 0 && (
                      <Row
                        label="Pagos recibidos"
                        value={`-${money(inv.payments_total)}`}
                      />
                    )}
                    {inv.credit_notes_total > 0 && (
                      <Row
                        label="Credit Notes"
                        value={`-${money(inv.credit_notes_total)}`}
                      />
                    )}
                    <div
                      className={`border-t border-white/20 pt-2 flex justify-between text-2xl font-black ${
                        inv.balance_due > 0
                          ? "text-[var(--accent-magenta,#E5007D)]"
                          : "text-emerald-400"
                      }`}
                    >
                      <span>BALANCE</span>
                      <span data-testid="invoice-detail-balance">
                        {money(inv.balance_due)}
                      </span>
                    </div>
                  </div>
                </Section>

                <ClientFilesSection docType="invoices" docId={inv.id} />

                <Section title="Historial">
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {(inv.history || [])
                      .slice()
                      .reverse()
                      .map((h) => (
                        <div
                          key={h.id || h.at}
                          className="text-xs border-l-2 border-[var(--border)] pl-2"
                        >
                          <div className="font-mono-tech font-bold uppercase">
                            {h.action}
                          </div>
                          <div className="text-[var(--text-secondary)]">
                            {String(h.at || "").slice(0, 16).replace("T", " ")}
                            {h.by_name ? ` · ${h.by_name}` : ""}
                          </div>
                        </div>
                      ))}
                    {(!inv.history || inv.history.length === 0) && (
                      <div className="text-xs text-[var(--text-secondary)]">Sin eventos.</div>
                    )}
                  </div>
                </Section>
              </div>
            </div>
          </>
        )}
      </div>

      {/* GP 1.0 · Modal de Work Order (detalle Taller) */}
      {woModalOpen && woOrder && (
        <OrderDetailsModal
          open={woModalOpen}
          order={woOrder}
          employees={woEmployees}
          onClose={() => {
            setWoModalOpen(false);
            setWoOrder(null);
          }}
          onChanged={() => {
            onChanged && onChanged();
          }}
        />
      )}
    </div>
  );
}

// Fase 7A — editor de costo por línea (solo Admin).
function ItemCostRow({ invoiceId, item, idx, onSaved, t }) {
  const pending =
    !(Number(item.cost_total || 0) > 0 ||
      Number(item.unit_cost || 0) > 0 ||
      item.cost_confirmed === true);
  const [cost, setCost] = useState(
    Number(item.unit_cost || 0) > 0 ? String(item.unit_cost) : ""
  );
  const [confirmZero, setConfirmZero] = useState(false);
  const [saving, setSaving] = useState(false);
  const qty = Number(item.quantity || 0);
  const num = Number(cost || 0);
  const save = async () => {
    if (Number.isNaN(num) || num < 0) {
      toast.error(t("inv_cost_save_err"));
      return;
    }
    if (num === 0 && !confirmZero) {
      toast.error(t("inv_cost_confirm_zero_hint"));
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/commercial/invoices/${invoiceId}/items/${item.id}/cost`,
        { unit_cost: num, confirm_zero: confirmZero },
        { withCredentials: true }
      );
      toast.success(t("inv_cost_saved"));
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("inv_cost_save_err"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid={`invoice-cost-row-${idx}`} className="p-3 space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 text-sm font-bold truncate">
          {item.description || item.product_code}
          <span className="font-normal text-xs text-[var(--text-secondary)]"> · ×{qty}</span>
        </div>
        <span
          data-testid={`invoice-cost-status-${idx}`}
          className={`font-mono-tech text-[0.65rem] font-bold px-2 py-1 border ${
            pending
              ? "bg-amber-100 text-amber-900 border-amber-400"
              : "bg-emerald-100 text-emerald-900 border-emerald-400"
          }`}
        >
          {pending ? t("inv_cost_pending") : t("inv_cost_recorded")}
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block font-mono-tech text-[0.6rem] font-bold tracking-wider text-[var(--text-secondary)] mb-1">
            {t("inv_cost_unit")}
          </label>
          <input
            data-testid={`invoice-cost-input-${idx}`}
            type="number"
            min="0"
            step="0.01"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className="border border-[var(--border)] px-2 py-1.5 text-sm w-28 font-mono-tech"
          />
        </div>
        <div className="text-xs font-mono-tech text-[var(--text-secondary)] pb-2">
          {qty} × {money(num)} = <b className="text-[var(--text)]">{money(qty * num)}</b>
        </div>
        <label className="flex items-center gap-1.5 text-xs pb-2 cursor-pointer">
          <input
            data-testid={`invoice-cost-zero-${idx}`}
            type="checkbox"
            checked={confirmZero}
            onChange={(e) => setConfirmZero(e.target.checked)}
          />
          {t("inv_cost_confirm_zero")}
        </label>
        <button
          data-testid={`invoice-cost-save-${idx}`}
          onClick={save}
          disabled={saving}
          className="font-mono-tech text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors disabled:opacity-40"
        >
          {t("inv_cost_save")}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="label-overline text-[var(--text-secondary)] mb-2">
        {title.toUpperCase()}
      </div>
      {children}
    </section>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono-tech text-[0.65rem] font-bold tracking-wider text-[var(--text-secondary)] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
function ActionBtn({ children, testid, onClick, primary, danger }) {
  const cls = primary
    ? "font-mono-tech text-xs font-bold bg-black text-white px-3 py-2 border border-black hover:bg-[var(--accent-magenta,#E5007D)] transition-colors"
    : danger
    ? "font-mono-tech text-xs font-bold border border-red-600 text-red-600 px-3 py-2 hover:bg-red-600 hover:text-white transition-colors"
    : "font-mono-tech text-xs font-bold border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors";
  return (
    <button data-testid={testid} onClick={onClick} className={cls}>
      {children}
    </button>
  );
}
